/**
 * Error Handler
 * Handles error logging and status code specific handling
 */

import type { AxiosError } from "axios";
import { createApiError } from "../../errors";
import { HttpStatusCode, type InterceptorConfig } from "../../types";
import { shouldLog } from "../../utils/logging";

/**
 * Handles error logging and status code specific messages
 */
export function handleErrorLogging(
  error: AxiosError,
  config: InterceptorConfig,
): void {
  const apiError = createApiError(error);

  if (shouldLog(config)) {
    console.error(
      `[API Error] ${error.response?.status || "Network"} ${error.config?.method?.toUpperCase()} ${error.config?.url}`,
      {
        message: apiError.message,
        type: apiError.type,
      },
    );
  }

  // Handle specific status codes
  if (error.response?.status === HttpStatusCode.FORBIDDEN) {
    if (shouldLog(config)) {
      console.warn("[API] Access forbidden - insufficient permissions");
    }
  }

  if (error.response?.status === HttpStatusCode.TOO_MANY_REQUESTS) {
    const retryAfter = error.response.headers["retry-after"];
    if (shouldLog(config)) {
      console.warn(
        `[API] Rate limited. Retry after: ${retryAfter || "unknown"} seconds`,
      );
    }
  }
}

/**
 * Creates an ApiError from an AxiosError
 */
export function createErrorFromAxios(error: AxiosError) {
  return createApiError(error);
}
